/**
 * Data layer for `cr-optimization-rule-auto-action-on-cr-below-target`. Thin typed fetch wrappers over /api/v1/cr-optimization-rule-auto-action-on-cr-below-target.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export type CrRuleAction = "pause" | "reduce_cap" | "alert";

export interface CrRuleRow {
	id: string;
	tenant_id: string;
	rule_key: string;
	scope: string;
	target_cr: number;
	min_clicks: number;
	action: CrRuleAction;
	enabled: number;
	created_at: number;
}

export interface ListCrRulesResponse {
	items: CrRuleRow[];
}

export interface CreateCrRuleBody {
	rule_key: string;
	scope: string;
	target_cr: number;
	min_clicks: number;
	action: CrRuleAction;
}

export interface CreateCrRuleResponse {
	ok: true;
	rule_key: string;
	created_at: number;
}

export interface EvaluateCrRuleBody {
	rule_key: string;
	clicks: number;
	conversions: number;
}

export interface EvaluateCrRuleResponse {
	rule_key: string;
	scope: string;
	cr: number;
	target_cr: number;
	observed_sample: {
		clicks: number;
		conversions: number;
		min_clicks: number;
		enough_sample: boolean;
	};
	should_trigger: boolean;
	action_when_triggered: CrRuleAction | null;
}

const BASE = "/api/v1/cr-optimization-rule-auto-action-on-cr-below-target";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCrRulesResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCrRulesResponse>(res);
}

export async function create(body: CreateCrRuleBody): Promise<CreateCrRuleResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateCrRuleResponse>(res);
}

export async function evaluate(body: EvaluateCrRuleBody): Promise<EvaluateCrRuleResponse> {
	const res = await fetch(`${BASE}/evaluate`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<EvaluateCrRuleResponse>(res);
}

export const crOptimizationRuleAutoActionOnCrBelowTargetQueryKeys = {
	all: ["cr-optimization-rule-auto-action-on-cr-below-target"] as const,
	list: () => [...crOptimizationRuleAutoActionOnCrBelowTargetQueryKeys.all] as const,
	detail: (id: string) => [...crOptimizationRuleAutoActionOnCrBelowTargetQueryKeys.all, id] as const,
};
